import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import Link from "@tiptap/extension-link";
import { Placeholder } from "@tiptap/extension-placeholder";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import showdown from "showdown";
import { snackAdded } from "src/slices/system";
import Editor from "../../components/dashboard/editor";

const HumanizerEmbed = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [originalText, setOriginalText] = useState("");
  const [humanizedText, setHumanizedText] = useState("");
  const [showText, setShowText] = useState<"original" | "humanized">(
    "original"
  );
  const [limitReached, setLimitReached] = useState<"min" | "max" | null>(null);
  const [isWordsLimitReached, setIsWordsLimitReached] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [isResponseReceived, setIsResponseReceived] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: "Enter the text you want to humanize",
      }),
      Link.configure({
        openOnClick: true,
      }),
    ],
    content: "",
    editable: true,
    autofocus: "end",
  });

  useEffect(() => {
    setHumanizedText("");
    setShowOriginal(false);
    setIsWordsLimitReached(false);
    setShowText("original");
  }, []);

  useEffect(() => {
    const checkEditorContent = async () => {
      if (editor && editor.getText() === "") {
        setHumanizedText("");
        setShowOriginal(false);
        setLimitReached(null);
      }
      if (getWordCount(editor?.getText() || "") > 1000 && !isResponseReceived) {
        setDisableButton(true);
        setLimitReached("max");
      } else if (
        getWordCount(editor?.getText() || "") > 0 &&
        getWordCount(editor?.getText() || "") < 200 &&
        !isResponseReceived
      ) {
        setDisableButton(true);
        setLimitReached("min");
      } else {
        setDisableButton(false);
        setLimitReached(null);
      }
    };

    checkEditorContent();
  }, [editor?.getText()]);

  const handleStream = async () => {
    if (getWordCount(editor?.getText() || "") > 1000) {
      setIsWordsLimitReached(true);
      setDisableButton(true);
      await dispatch(
        snackAdded({
          message: "Please reduce the number of words to 1000",
          type: "error",
          duration: 2000,
        })
      );
      return;
    }

    setLoading(true);
    setOriginalText(editor?.getHTML() || "");
    
    // Feature temporarily disabled - comment out API call
    try {
      // Simulate loading time
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Set disabled message instead of making API call
      const disabledMessage = "<p>This feature is temporarily disabled.</p>";
      editor?.commands.setContent(disabledMessage);
      
      setIsResponseReceived(true);
      
      // API call commented out:
      /*
      let accumulatedText = "";
      const converter = new showdown.Converter();
      // const url = getCloudFrontUrl();
      //localhost
      // const url = "http://127.0.0.1:8000/api/v1/humanizer/website/stream";
      const url =
        "https://humanizer.seowind.io/api/v1/humanizer/website/stream";
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          text: editor?.getHTML() || "",
          source: "humanizer_app",
        }),
      });

      if (response.status == 429) {
        const data = await response.json();
        editor?.commands.setContent(data.message);
        setLoading(false);
        setShowOriginal(true);
        setShowText("original");
        setDisableButton(false);
        return;
      }
      const reader = response.body?.getReader();
      const decoder = new TextDecoder("utf-8");

      let done = false;
      setIsResponseReceived(true);
      while (!done) {
        const { value, done: doneReading } = await reader?.read()!;
        done = doneReading;
        const chunk = decoder.decode(value, { stream: true });
        accumulatedText += chunk;
        if (accumulatedText.endsWith("BREAK")) {
          accumulatedText = accumulatedText.slice(0, -5);
          accumulatedText += "\n";
        }

        let html = converter.makeHtml(accumulatedText);
        editor?.commands.setContent(html);
      }
      */
    } catch (error) {
      console.error("Error streaming data:", error);
    } finally {
      setLoading(false);
      setShowOriginal(true);
      setHumanizedText(editor?.getHTML() || "");
      setShowText("humanized");
      setDisableButton(false);
    }
  };

  const handleCopyToClipboard = async () => {
    if (editor) {
      const content = editor.getHTML(); // Get the HTML content from the editor
      try {
        const blob = new Blob([content], { type: "text/html" });
        const data = [new ClipboardItem({ "text/html": blob })];
        await navigator.clipboard.write(data);
        dispatch(
          snackAdded({
            message: "Content copied to clipboard!",
            type: "success",
            duration: 2000,
          })
        );
      } catch (err) {
        console.error("Failed to copy: ", err);
        dispatch(
          snackAdded({
            message: "Failed to copy content. Please try again.",
            type: "error",
            duration: 1000,
          })
        );
      }
    }
  };
  const handleShowOriginal = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      editor?.commands.setContent(originalText);
      setShowText("original");
    } else {
      editor?.commands.setContent(humanizedText);
      setShowText("humanized");
    }
  };

  const getWordCount = (text: string) => {
    return text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  };

  const editorText = editor?.getText() || "";
  const wordCount = getWordCount(editorText);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        // width: "calc(100% - 420px)",
        // margin: "auto",
        width: "70%",
        margin: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          margin: "auto",
        }}
      >
        <Box sx={{ margin: "auto" }}>
          <Typography
            color="#121828"
            fontFamily="Inter"
            fontSize="18px"
            fontWeight="600"
          >
            AI Humanizer & Proofreader
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            margin: "0px auto",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              mt: 2,
              ml: "3.5rem",
            }}
          >
            <Typography
              color="#121828"
              fontFamily="Inter"
              fontSize="14px"
              fontStyle="normal"
              fontWeight="400"
              lineHeight="normal"
            >
              {showText === "original"
                ? "Original content"
                : "Humanized content"}
            </Typography>
          </Box>
          {showOriginal && (
            <Box
              sx={{ display: "flex", justifyContent: "flex-end", mr: "3.5rem" }}
            >
              <FormControlLabel
                control={<Switch />}
                label="Show original"
                onChange={handleShowOriginal}
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontWeight: "400",
                  },
                }}
              />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            height: "calc(100vh - 150px)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: 2,
            margin: "auto",
            paddingTop: "0.5rem",
          }}
        >
          <Editor editor={editor} isHumanizer={true} />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              marginTop: "-5px",
              justifyContent: "space-between",
              marginLeft: "3.5rem",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {limitReached && (
                <Typography
                  color="red"
                  /* Heading/H1 */
                  fontFamily="Inter"
                  fontSize="14px"
                  fontStyle="normal"
                  fontWeight="400"
                  lineHeight="normal"
                >
                  {limitReached === "min"
                    ? "Please enter at least 200 words to humanize"
                    : "Please reduce the number of words to 1000"}
                </Typography>
              )}
              <Typography
                color={"#121828"}
                /* Heading/H1 */
                fontFamily="Inter"
                fontSize="14px"
                fontStyle="normal"
                fontWeight="400"
                lineHeight="normal"
                sx={{
                  mt: "0.2rem",
                }}
              >
                {humanizedText
                  ? `${wordCount} words`
                  : `${wordCount} words / 1000 words`}
              </Typography>
            </Box>

            {!humanizedText && (
              <Button
                variant="contained"
                color={loading ? "secondary" : "primary"}
                disabled={loading || disableButton}
                onClick={handleStream}
                sx={{
                  minWidth: 140,
                  py: 1,
                  mr: "3.5rem",
                }}
              >
                {loading ? (
                  <CircularProgress size={20} sx={{ color: "gray" }} />
                ) : (
                  "Humanize"
                )}
              </Button>
            )}
            {humanizedText && (
              <Box sx={{ display: "flex", flexDirection: "row" }}>
                <Button
                  variant="text"
                  onClick={() => {
                    editor?.commands.setContent("");
                    setHumanizedText("");
                    setShowOriginal(false);
                    setShowText("original");
                    setIsResponseReceived(false);
                    setLimitReached(null);
                  }}
                  sx={{
                    border: "0 !important",
                    mr: "1.5rem",
                    cursor: "pointer",
                    color: "#121828",
                    fontSize: "15px",
                    fontWeight: "500",
                  }}
                >
                  Clear
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    minWidth: 140,
                    py: 1,
                    mr: "3.5rem",
                  }}
                  onClick={handleCopyToClipboard}
                >
                  Copy text
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default HumanizerEmbed;
